import { AssistanceAudience, AssistanceResource } from '../interfaces/assistance-resource';

/**
 * Ce que la gestion et la consultation de l'aide doivent dire pareil : la
 * nature d'une ressource et le décompte d'une série. Deux copies de ces règles
 * finiraient par afficher une vidéo d'un côté et un lien de l'autre pour la
 * même ressource. Ce qu'une URL a le droit de devenir est dans
 * `external-link`, commun à tous les liens externes.
 */

/** Les trois publics, dans l'ordre des onglets : l'équipe d'abord. */
export const AUDIENCES: readonly AssistanceAudience[] = ['Admin', 'Professor', 'Student'];

export type ResourceKind = 'video' | 'pdf' | 'image' | 'link' | 'file';

export interface SeriesSummary {
  videos: number;
  documents: number;
  links: number;
}

const YOUTUBE = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
const VIMEO = /vimeo\.com\/(\d+)/;

export function isAudience(value: string | null | undefined): value is AssistanceAudience {
  return AUDIENCES.includes(value as AssistanceAudience);
}

export function audienceLabelKey(audience: AssistanceAudience): string {
  switch (audience) {
    case 'Admin': return 'ASSISTANCE.AUDIENCE_ADMIN';
    case 'Professor': return 'ASSISTANCE.AUDIENCE_PROFESSOR';
    default: return 'ASSISTANCE.AUDIENCE_STUDENT';
  }
}

export function audienceIcon(audience: AssistanceAudience): string {
  switch (audience) {
    case 'Admin': return 'fa-solid fa-user-shield';
    case 'Professor': return 'fa-solid fa-user-tie';
    default: return 'fa-solid fa-user-graduate';
  }
}

/** Suffixe de classe de couleur : ambre pour l'équipe, bleu et vert comme avant. */
export function audienceClass(audience: AssistanceAudience): 'admin' | 'prof' | 'stud' {
  switch (audience) {
    case 'Admin': return 'admin';
    case 'Professor': return 'prof';
    default: return 'stud';
  }
}

/** Adresse lisible dans une iframe pour une vidéo YouTube ou Vimeo, sinon `null`. */
export function embedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const youtube = url.match(YOUTUBE);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1&rel=0`;
  const vimeo = url.match(VIMEO);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
}

export function resourceKind(resource: Pick<AssistanceResource, 'contentType' | 'externalUrl' | 'hasFile'>): ResourceKind {
  const type = resource.contentType ?? '';
  if (type.startsWith('video/')) return 'video';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (!resource.hasFile && resource.externalUrl) return embedUrl(resource.externalUrl) ? 'video' : 'link';
  return 'file';
}

export function kindIcon(resource: Pick<AssistanceResource, 'contentType' | 'externalUrl' | 'hasFile'>): string {
  switch (resourceKind(resource)) {
    case 'video': return 'fa-solid fa-circle-play';
    case 'pdf': return 'fa-solid fa-file-pdf';
    case 'image': return 'fa-solid fa-image';
    case 'link': return 'fa-solid fa-link';
    default: return 'fa-solid fa-file';
  }
}

/** Décompte d'une série par nature — ce que résume l'en-tête d'un module. */
export function summarize(resources: readonly AssistanceResource[]): SeriesSummary {
  return resources.reduce<SeriesSummary>(
    (acc, resource) => {
      const kind = resourceKind(resource);
      if (kind === 'video') acc.videos++;
      else if (kind === 'link') acc.links++;
      else acc.documents++;
      return acc;
    },
    { videos: 0, documents: 0, links: 0 },
  );
}

export function formatSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
